import { useId, useState, type ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { Switch } from '@/components/ui/switch';
import { formatDate } from '@/util/dateUtils';

import JsonBlock from './JsonBlock';
import type { McpTool } from '../hooks/useMcpServers';

/** "Dec 17th, 2025, 2:53:36 PM" — the build date format the design spells out. */
const BUILD_DATE_FORMAT = 'MMM do, yyyy, h:mm:ss a';

type InfoBlockProps = { label: string; children: ReactNode };

const InfoBlock = ({ label, children }: InfoBlockProps) => (
  <div className="flex flex-col">
    <span className="text-xs leading-5 text-foreground-muted">{label}</span>
    {children}
  </div>
);

const InfoValue = ({ children }: { children: ReactNode }) => (
  <span className="text-sm leading-5 font-medium text-foreground">{children}</span>
);

/**
 * The description is the one free-text field a server controls, and it is routinely far longer
 * than the name or the build date `InfoValue` is shaped for — servers inline usage notes, markdown
 * bullets, JSON examples, even a whole GraphQL SDL, because a tool's `inputSchema` cannot express
 * what a valid argument looks like when the argument is one opaque string.
 *
 * So it needs two things the shared `InfoValue` deliberately does not have:
 *
 * - `whitespace-pre-wrap` keeps the author's line breaks and indentation. Default collapsing turns
 *   a multi-line description into one run-on paragraph. `pre-line` would keep the newlines but eat
 *   the indentation of an embedded example.
 * - `break-words` breaks a long unbreakable token instead of overflowing. The blocks below sit in
 *   a single `overflow-y-auto` container, and an unset `overflow-x` computes to `auto` next to it —
 *   so without this an over-wide description puts a horizontal scrollbar on the whole column and
 *   drags Name and Build date sideways with it.
 */
const DescriptionValue = ({ children }: { children: string }) => (
  <p className="text-sm leading-5 font-normal break-words whitespace-pre-wrap text-foreground">
    {children}
  </p>
);

type ToolDetailsTabProps = { tool: McpTool; buildDate?: string };

const ToolDetailsTab = ({ tool, buildDate }: ToolDetailsTabProps) => {
  const { t } = useTranslation();
  const [previewJson, setPreviewJson] = useState(false);
  const switchId = useId();

  return (
    <>
      {/* The label/switch pair is built by hand: `Switch`'s own `label` prop renders
          `font-normal` and the design asks for `font-medium`. */}
      <div className="flex shrink-0 items-center gap-2">
        <label htmlFor={switchId} className="text-sm leading-none font-medium text-foreground">
          {t('mcp.preview_json')}
        </label>
        <Switch id={switchId} checked={previewJson} onCheckedChange={setPreviewJson} />
      </div>

      {previewJson ? (
        <JsonBlock className="min-h-0 flex-1">
          {JSON.stringify(tool.inputSchema ?? {}, null, 2)}
        </JsonBlock>
      ) : (
        /* Same `-mx-2 px-2` gutter as the Test tab: it keeps the copy button's focus ring off the
           container edge, and keeps the scrollbar in the same place when the tabs switch. */
        <div className="-mx-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-2">
          <InfoBlock label={t('common.name')}>
            <div className="flex items-center gap-1.5">
              <InfoValue>{tool.name}</InfoValue>
              {/* 20×20, not the `icon-sm` default of 32×32: at 32 the row grows past the 20px
                  line height and pushes every block below it down. */}
              <CopyButton data={tool.name} buttonSize="icon-xs" />
            </div>
          </InfoBlock>

          {buildDate && (
            <InfoBlock label={t('mcp.build_date')}>
              <InfoValue>{formatDate(buildDate, BUILD_DATE_FORMAT)}</InfoValue>
            </InfoBlock>
          )}

          {tool.description && (
            <InfoBlock label={t('common.description')}>
              <DescriptionValue>{tool.description}</DescriptionValue>
            </InfoBlock>
          )}
        </div>
      )}
    </>
  );
};

export default ToolDetailsTab;
